import * as api from "../apis/lineItemsApi";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  queryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";


// Query options

const getPaginatedLineItemsQueryOptions = ({
  filterText,
  filterStartDate,
  filterEndDate,
  filterAdvertiser,
  filterBrand,
  filterPlatform,
  filterAdFormat,
  filterCountry,
  filterDeleted,
  sortBy,
  page,
  nbDocsPerPage,
}) =>
  queryOptions({
    queryKey: [
      "lineItems",
      filterText,
      filterStartDate,
      filterEndDate,
      filterAdvertiser,
      filterBrand,
      filterPlatform,
      filterAdFormat,
      filterCountry,
      filterDeleted,
      sortBy,
      page,
      nbDocsPerPage,
    ],
    queryFn: () =>
      api.getPaginatedLineItems({
        filterText,
        filterStartDate,
        filterEndDate,
        filterAdvertiser,
        filterBrand,
        filterPlatform,
        filterAdFormat,
        filterCountry,
        filterDeleted,
        sortBy,
        page,
        nbDocsPerPage,
      }),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: "always",
    
  });

export const getLineItemsByCampaignQueryOptions = (campaignId) =>
  queryOptions({
    queryKey: ["campaign.lineItems", campaignId],
    queryFn: () => api.getLineItemsByCampaign(campaignId),
    refetchOnWindowFocus: "always",
    
    enabled: !!campaignId
  });

export const getLineItemsByOfferQueryOptions = (offerId) =>
  queryOptions({
    queryKey: ["offer.lineItems", offerId],
    queryFn: () => api.getLineItemsByOffer(offerId),
    refetchOnWindowFocus: "always",
    
  });

export const getLineItemQueryOptions = (id, queryClient) => {
  let queryKey;

  return queryOptions({
    queryKey: ["lineItem", id],
    queryFn: () => api.getLineItem(id),
    refetchOnWindowFocus: "always",
    
    enabled: !!id,
    initialData: () => {
      // we try to find the lineItem in one of the "lineItems" queries
      let found;

      queryClient
        .getQueriesData({ queryKey: ["lineItems"] })
        .forEach((query) => {
          const data = query[1]?.data;
          if (data) {
            data.forEach((lineItem) => {
              if (lineItem.id === id) {
                found = lineItem;
                queryKey = query[0];
              }
            });
          }
        });

      // otherwise, we try to find the lineItem in one of the "campaign.lineItems" queries

      if (!found) {
        queryClient
          .getQueriesData({ queryKey: ["campaign.lineItems"] })
          .forEach((query) => {
            const data = query[1]
            if (data) {
              data.forEach((lineItem) => {
                if (lineItem.id === id) {
                  found = lineItem;
                  queryKey = query[0];
                }
              });
            }
          });
      }

      // otherwise, we try to find the lineItem in one of the "offer.lineItems" queries

      if (!found) {
        queryClient
          .getQueriesData({ queryKey: ["offer.lineItems"] })
          .forEach((query) => {
            const data = query[1]
            if (data) {
              data.forEach((lineItem) => {
                if (lineItem.id === id) {
                  found = lineItem;
                  queryKey = query[0];
                }
              });
            }
          });
      }

      return found;
    },
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(queryKey)?.dataUpdatedAt,
  });
};

const getLineItemStatsByGenderQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByGender", lineItemId],
    queryFn: () => api.getLineItemStatsByGender(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByAgeQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByAge", lineItemId],
    queryFn: () => api.getLineItemStatsByAge(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByPlacementQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByPlacement", lineItemId],
    queryFn: () => api.getLineItemStatsByPlacement(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByPlatformQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByPlatform", lineItemId],
    queryFn: () => api.getLineItemStatsByPlatform(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByDevicePlatformQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByDevicePlatform", lineItemId],
    queryFn: () => api.getLineItemStatsByDevicePlatform(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByDeviceQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByDevice", lineItemId],
    queryFn: () => api.getLineItemStatsByDevice(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByAdQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByAd", lineItemId],
    queryFn: () => api.getLineItemStatsByAd(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByAdSetQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByAdSet", lineItemId],
    queryFn: () => api.getLineItemStatsByAdSet(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

const getLineItemStatsByDayQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.statsByDay", lineItemId],
    queryFn: () => api.getLineItemStatsByDay(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

export const getLineItemDataCheckQueryOptions = (lineItemId) =>
  queryOptions({
    queryKey: ["lineItem.dataCheck", lineItemId],
    queryFn: () => api.getLineItemDataCheck(lineItemId),
    refetchOnWindowFocus: "always",
    
  });

// useQuery

export const useGetPaginatedLineItems = ({
  filterText,
  filterStartDate,
  filterEndDate,
  filterAdvertiser,
  filterBrand,
  filterPlatform,
  filterAdFormat,
  filterCountry,
  filterDeleted,
  sortBy,
  page,
  nbDocsPerPage,
}) =>
  useQuery(
    getPaginatedLineItemsQueryOptions({
      filterText,
      filterStartDate,
      filterEndDate,
      filterAdvertiser,
      filterBrand,
      filterPlatform,
      filterAdFormat,
      filterCountry,
      filterDeleted,
      sortBy,
      page,
      nbDocsPerPage,
    })
  );

export const useGetLineItemsByCampaign = (campaignId) =>
  useQuery(getLineItemsByCampaignQueryOptions(campaignId));

export const useGetLineItemsByOffer = (offerId) =>
  useQuery(getLineItemsByOfferQueryOptions(offerId));

export const useGetLineItem = (id) => {
  const queryClient = useQueryClient();
  return useQuery(getLineItemQueryOptions(id, queryClient));
};

export const useGetLineItemStatsByGender = (lineItemId) =>
  useQuery(getLineItemStatsByGenderQueryOptions(lineItemId));

export const useGetLineItemStatsByAge = (lineItemId) =>
  useQuery(getLineItemStatsByAgeQueryOptions(lineItemId));

export const useGetLineItemStatsByPlacement = (lineItemId) =>
  useQuery(getLineItemStatsByPlacementQueryOptions(lineItemId));

export const useGetLineItemStatsByPlatform = (lineItemId) =>
  useQuery(getLineItemStatsByPlatformQueryOptions(lineItemId));

export const useGetLineItemStatsByDevicePlatform = (lineItemId) =>
  useQuery(getLineItemStatsByDevicePlatformQueryOptions(lineItemId));

export const useGetLineItemStatsByDevice = (lineItemId) =>
  useQuery(getLineItemStatsByDeviceQueryOptions(lineItemId));

export const useGetLineItemStatsByAd = (lineItemId) =>
  useQuery(getLineItemStatsByAdQueryOptions(lineItemId));

export const useGetLineItemStatsByAdSet = (lineItemId) =>
  useQuery(getLineItemStatsByAdSetQueryOptions(lineItemId));

export const useGetLineItemStatsByDay = (lineItemId) =>
  useQuery(getLineItemStatsByDayQueryOptions(lineItemId));

// useSuspenseQuery

export const useSuspenseGetLineItemsByCampaign = (campaignId) =>
  useSuspenseQuery(getLineItemsByCampaignQueryOptions(campaignId));

export const useSuspenseGetLineItemsByOffer = (offerId) =>
  useSuspenseQuery(getLineItemsByOfferQueryOptions(offerId));

export const useSuspenseGetLineItem = (id) => {
  const queryClient = useQueryClient();
  return useSuspenseQuery(getLineItemQueryOptions(id, queryClient));
};

export const useSuspenseGetLineItemDataCheck = (lineItemId) => {
  const queryClient = useQueryClient();
  return useSuspenseQuery(
    getLineItemDataCheckQueryOptions(lineItemId, queryClient)
  );
};


